import React from "react";
import { FormattedMessage } from "react-intl";

import { featureFlags } from "~/common/feature-flags";

import {
  type AudioNoteControllerProps as Props,
  type PatientAudioNoteValue,
  useAudioNoteController,
} from "../patient_shared/use-audio-note-controller";

export type { PatientAudioNoteValue };

export function PatientNewAudioNote(props: Props) {
  const {
    isLocked,
    isUploading,
    isRecording,
    hasDurationLimitReached,
    recordingDurationSeconds,
    totalDurationFormatted,
    maxDurationFormatted,
    liveTotalDurationFormatted,
    uploadProgressPercent,
    errorMessage,
    removeDisabledTooltip,
    allAudioEntries,
    playingAudioId,
    setPlayingAudioId,
    fileInputRef,
    setAudioRef,
    formatDuration,
    formatUploadTimestamp,
    startRecording,
    stopRecording,
    handleFileUpload,
    toggleAudioPlayback,
    removeAudioEntry,
    openFilePicker,
  } = useAudioNoteController(props);

  // Экспертное сопровождение гейтится единым фича-флагом.
  if (!featureFlags.smartPrescription) {
    return null;
  }

  return (
    <div id="smart-audio-note" className="form-group">
      <h3 className="block" style={{ fontWeight: "900" }}>
        <FormattedMessage id="additional.services.smart.audio.recipe_title" />
      </h3>

      <div className="patient-audio-note__recording-shell">
        <div className="patient-audio-note__recording-row">
          <button
            type="button"
            className={`patient-audio-note__record-button ${
              isRecording ? "patient-audio-note__record-button--active" : ""
            }`}
            disabled={isLocked || isUploading || hasDurationLimitReached}
            onClick={isRecording ? stopRecording : startRecording}
            aria-label={isRecording ? "stop-recording" : "start-recording"}
          >
            <i className={isRecording ? "icon-control-pause" : "icon-microphone"} />
          </button>

          <div className="patient-audio-note__recording-content">
            {isRecording ? (
              <>
                <div className="patient-audio-note__recording-status-row">
                  <div className="patient-audio-note__recording-status">
                    <span className="patient-audio-note__recording-dot" />
                    <FormattedMessage id="additional.services.smart.audio.recording_now" />
                  </div>
                  <div className="patient-audio-note__recording-timer patient-audio-note__recording-timer--inline">
                    {formatDuration(recordingDurationSeconds)}
                  </div>
                </div>
                <div className="patient-audio-note__waveform" aria-hidden>
                  {Array.from({ length: 72 }).map((_, index) => (
                    <span
                      key={`wave-${index}`}
                      className="patient-audio-note__waveform-bar"
                      style={{ animationDelay: `${index * 0.07}s` }}
                    />
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="patient-audio-note__recording-title">
                  <FormattedMessage id="additional.services.smart.audio.start_recording" />
                </div>
                <div className="patient-audio-note__recording-subtitle">
                  <FormattedMessage id="additional.services.smart.audio.recording_hint" />
                </div>
                <div className="patient-audio-note__waveform patient-audio-note__waveform--idle" />
              </>
            )}
          </div>

          <div className="patient-audio-note__recording-timer">
            {isRecording ? null : totalDurationFormatted}
          </div>
        </div>
        <div className="patient-audio-note__duration-limit">
          {liveTotalDurationFormatted} / {maxDurationFormatted}
        </div>
      </div>

      {errorMessage ? (
        <p className="tw-mt-2 tw-text-sm tw-text-[#d9534f]">
          <FormattedMessage id={errorMessage} />
        </p>
      ) : null}

      <div className="tw-mt-2">
        <button
          type="button"
          className="btn btn-default btn-sm"
          disabled={isLocked || isUploading || hasDurationLimitReached}
          onClick={openFilePicker}
        >
          <FormattedMessage id="additional.services.smart.audio.upload" />
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".ogg,.oga,.opus,.mp3,.wav,.pcm,audio/ogg,audio/mpeg,audio/wav,audio/x-wav,audio/opus"
        multiple
        style={{ display: "none" }}
        onChange={handleFileUpload}
      />

      {isUploading ? (
        <div className="patient-audio-note__upload-progress">
          <p className="tw-text-sm tw-text-[#5c6670]">
            <FormattedMessage id="additional.services.smart.audio.uploading" />
          </p>
          <div className="patient-audio-note__upload-progress-track">
            <div
              className="patient-audio-note__upload-progress-fill"
              style={{ width: `${uploadProgressPercent}%` }}
            />
          </div>
        </div>
      ) : null}

      {allAudioEntries.length > 0 ? (
        <div className="tw-mt-4">
          <div className="patient-audio-note__list-title">
            <FormattedMessage id="additional.services.smart.audio.records_title" />
          </div>
          <div className="patient-audio-note__list">
            {allAudioEntries.map((entry, index) => {
              const durationText =
                typeof entry.durationSeconds == "number"
                  ? formatDuration(Math.max(0, entry.durationSeconds))
                  : "--:--";
              const isPlaying = playingAudioId === entry.id;

              return (
                <div key={entry.id} className="patient-audio-note__list-item">
                  <button
                    type="button"
                    className="patient-audio-note__play-button"
                    disabled={!entry.sourceUrl}
                    onClick={() => void toggleAudioPlayback(entry.id)}
                    aria-label={isPlaying ? "pause-audio" : "play-audio"}
                  >
                    <i className={isPlaying ? "icon-control-pause" : "icon-control-play"} />
                  </button>
                  <div className="patient-audio-note__list-text">
                    <div className="patient-audio-note__list-name">
                      {`Новая запись ${allAudioEntries.length - index}`}
                    </div>
                    <div className="patient-audio-note__list-date">
                      {formatUploadTimestamp(entry.uploadedAt)}
                    </div>
                  </div>
                  <div className="patient-audio-note__list-duration">{durationText}</div>
                  <button
                    type="button"
                    className="patient-audio-note__remove-button"
                    disabled={!entry.isRemovable}
                    onClick={() => removeAudioEntry(entry.id)}
                    aria-label="remove-audio"
                    title={entry.isRemovable ? undefined : removeDisabledTooltip}
                  >
                    <i className="icon-close" />
                  </button>

                  {entry.sourceUrl ? (
                    // eslint-disable-next-line jsx-a11y/media-has-caption -- doctor audio playback only
                    <audio
                      ref={(node) => {
                        setAudioRef(entry.id, node);
                      }}
                      src={entry.sourceUrl}
                      style={{ display: "none" }}
                      onEnded={() => {
                        if (playingAudioId === entry.id) {
                          setPlayingAudioId(null);
                        }
                      }}
                    />
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
